import type React from 'react'
import type { JSX, RefObject } from 'react'

import { useCallback, useEffect, useRef, useState } from 'react'
import { CardsContainerSC } from '../CardsContainer.styled'

export interface ILazyLoadProps {
  onLoadMore?: () => void
  percentToTrigger?: number
  children: React.ReactNode
  dataTestId?: string
}

const getHeights = (containerRef: RefObject<HTMLDivElement | null>) => {
  const viewPortHeight = Math.max(
    document.documentElement.clientHeight || 0,
    window.innerHeight || 0
  )

  const containerHeight = Number(containerRef.current?.offsetHeight)
  const rect = containerRef.current?.getBoundingClientRect()
  const bottom = Number(rect?.bottom)

  const pixelsToContainerEnd = bottom - viewPortHeight
  return { pixelsToContainerEnd, containerHeight, viewPortHeight }
}

export const LazyLoad = ({
  onLoadMore,
  percentToTrigger, // this is percent of viewport height, that is under bottom view border
  children,
  dataTestId,
}: ILazyLoadProps): JSX.Element => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [heightIsTriggered, setHeightIsTriggered] = useState(0) // save last already triggered container height

  const calcScrolledPercent = useCallback(() => {
    const { pixelsToContainerEnd, containerHeight, viewPortHeight } =
      getHeights(containerRef)

    if (
      percentToTrigger &&
      Number(percentToTrigger) > 0 &&
      Number(percentToTrigger) <= 100
    ) {
      const pixelPercents = (viewPortHeight * percentToTrigger) / 100
      if (
        pixelsToContainerEnd <= pixelPercents &&
        heightIsTriggered !== containerHeight
      ) {
        setHeightIsTriggered(containerHeight)
        onLoadMore?.()
      }
      return
    }

    if (pixelsToContainerEnd <= 0) {
      // case when no percentToTrigger and container is scrollable
      setHeightIsTriggered(containerHeight)
      onLoadMore?.()
    }
  }, [onLoadMore, percentToTrigger, heightIsTriggered])

  useEffect(() => {
    // this effect handles case when content height is less than viewport (no scroll)
    const { pixelsToContainerEnd, containerHeight } = getHeights(containerRef)

    if (pixelsToContainerEnd <= 0) {
      // negative pixelsToContainerEnd means "content height is less than viewport"
      if (heightIsTriggered !== containerHeight) {
        // check call were done for this height
        setHeightIsTriggered(containerHeight)
        onLoadMore?.()
      }
    }
  }, [onLoadMore, heightIsTriggered])

  useEffect(() => {
    window.addEventListener('scroll', calcScrolledPercent)
    window.addEventListener('touchmove', calcScrolledPercent)
    return () => {
      window.removeEventListener('scroll', calcScrolledPercent)
      window.removeEventListener('touchmove', calcScrolledPercent)
    }
  }, [calcScrolledPercent])

  return (
    <CardsContainerSC ref={containerRef} data-testid={dataTestId}>
      {children}
    </CardsContainerSC>
  )
}

export const LazyLoadExternal = ({
  onLoadMore,
  percentToTrigger, // this is percent of viewport height, that is under bottom view border
  children,
  onSaveState,
  state,
}: {
  onLoadMore?: () => void
  percentToTrigger?: number
  children: React.ReactNode
  state: number
  onSaveState: (state: number) => void
}): JSX.Element => {
  const containerRef = useRef<HTMLDivElement>(null)
  const heightIsTriggered = state

  const calcScrolledPercent = useCallback(() => {
    const { pixelsToContainerEnd, containerHeight, viewPortHeight } =
      getHeights(containerRef)

    if (
      percentToTrigger &&
      Number(percentToTrigger) > 0 &&
      Number(percentToTrigger) <= 100
    ) {
      const pixelPercents = (viewPortHeight * percentToTrigger) / 100
      if (
        pixelsToContainerEnd <= pixelPercents &&
        heightIsTriggered !== containerHeight
      ) {
        onSaveState(containerHeight)
        onLoadMore?.()
      }
      return
    }

    if (pixelsToContainerEnd <= 0) {
      // case when no percentToTrigger and container is scrollable
      onSaveState(containerHeight)
      onLoadMore?.()
    }
  }, [onLoadMore, percentToTrigger, heightIsTriggered, onSaveState])

  useEffect(() => {
    // this effect handles case when content height is less than viewport (no scroll)
    const { pixelsToContainerEnd, containerHeight } = getHeights(containerRef)

    if (pixelsToContainerEnd <= 0) {
      // negative pixelsToContainerEnd means "content height is less than viewport"
      if (heightIsTriggered !== containerHeight) {
        // check call were done for this height
        onSaveState(containerHeight)
        onLoadMore?.()
      }
    }
  }, [onLoadMore, heightIsTriggered, onSaveState])

  useEffect(() => {
    window.addEventListener('scroll', calcScrolledPercent)
    window.addEventListener('touchmove', calcScrolledPercent)

    return () => {
      window.removeEventListener('scroll', calcScrolledPercent)
      window.removeEventListener('touchmove', calcScrolledPercent)
    }
  }, [calcScrolledPercent])

  return <CardsContainerSC ref={containerRef}>{children}</CardsContainerSC>
}
